const FIELD_LABELS: Record<string, string> = {
  name: "Name",
  canonical_name: "Name",
  city: "City",
  country: "Country",
  country_code: "Country",
  address: "Address",
  institution: "Institution",
  inst: "Institution",
  affiliation: "Affiliation",
  phone: "Phone",
  email: "Email",
  pi: "Principal Investigator",
  specialty: "Specialty",
  npi: "NPI",
  source: "Source",
  source_system: "Source System",
};

export function fieldLabel(key: string): string {
  return (
    FIELD_LABELS[key] ??
    key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

/** One column of the side-by-side field comparison in DiffModal. */
export type MemberRecord = {
  label: string;
  source: string | null;
  fields: Record<string, string>;
};

/** Member keys that identify or score the member rather than describing the
 *  entity — never shown as comparable field rows. */
const MEMBER_META_KEYS = new Set([
  "id",
  "source_system",
  "source_label",
  "confidence_pct",
  "confidence_tier",
  "scores",
]);

/**
 * Every contributing member of the golden record, as comparison columns.
 *
 * A conflict row's `raw_fields` carries only the conflicting field (often just
 * the name), so a two-column raw-vs-golden view hides everything the members
 * actually disagree on. Reading the members off the `comparison` payload
 * instead surfaces each one's full record, which is what a steward needs in
 * order to judge whether the member belongs to this golden record.
 *
 * Typed structurally rather than against ComparisonMember so this stays a pure
 * util: any extra field the backend adds shows up as a row automatically.
 */
export function membersFromComparison(
  comparison: {
    sides?: { members?: Record<string, unknown>[] }[];
  } | null,
): MemberRecord[] {
  if (!comparison) return [];
  return (comparison.sides ?? []).flatMap((side) =>
    (side.members ?? []).map((member) => {
      const fields: Record<string, string> = {};
      for (const [key, value] of Object.entries(member)) {
        if (MEMBER_META_KEYS.has(key)) continue;
        if (value == null || typeof value === "object") continue;
        const text = String(value).trim();
        if (text !== "") fields[key] = text;
      }
      const str = (v: unknown) => (typeof v === "string" ? v : "");
      return {
        label:
          str(member.name) || str(member.source_label) || str(member.id) || "—",
        source: str(member.source_label) || str(member.source_system) || null,
        fields,
      };
    }),
  );
}

export function scoreColor(score: number): { color: string; bg: string } {
  if (score >= 0.85) return { color: "#15803D", bg: "#DCFCE7" };
  if (score >= 0.65) return { color: "#B45309", bg: "#FEF3C7" };
  return { color: "#BE0303", bg: "#FEE2E2" };
}
